import React from 'react';

import { ThreeDStockMaterialsCompactListItemButtonsMaterial } from './ThreeDStockMaterialsCompactListItemButtons.types';
import { UpdateMaterialCacheAction } from '../../../../../../materials/materialsTypes';
import { IconsEnum } from '../../../../../../../assets/icons/types';

import useMaterialFavoriteButton from '../../../../../../materials/components/buttons/MaterialFavoriteButton/useMaterialFavoriteButton';

import { SubmitMaterialToProjectOrTaskModalButton } from '../../../../../../materials/components/lists/MaterialsListItem/components/SubmitMaterialToProjectOrTaskModalButton';
import { DownloadMaterialButton } from '../../../../../../materials/components/buttons/DownloadMaterialButton';
import { MaterialsCopyLinkButton } from '../../../../../../materials/components/buttons/MaterialsCopyLinkButton';
import { DownloadMaterialSeparatelyButton } from '../../../../../../materials/components/buttons/DownloadMaterialSeparatelyButton';

import { CheckPermissions } from '../../../../../../../helpers/CheckPermissions';
import { DropdownHelper } from '../../../../../../../helpers/dropdowns/DropdownHelper';
import { NextLinkHelper } from '../../../../../../../helpers/links/NextLinkHelper';
import { Icon } from '../../../../../../../helpers/Icon';
import { Translate } from '../../../../../../../helpers/Translate';
import { ButtonHelper } from '../../../../../../../helpers/buttons/ButtonHelper';

import { MaterialPath } from '../../../../../../materials/MaterialPath';

import { MaterialsPermissions } from '../../../../../../materials/materialsConstants';
import { PopoverPlacement } from '../../../../../../../helpers/Popover/popoverConstants';

import { words } from '../../../../../../../locales/keys';

interface ThreeDStockMaterialsCompactListItemButtonsProps {
  material: ThreeDStockMaterialsCompactListItemButtonsMaterial;
  updateMaterialCache?: UpdateMaterialCacheAction<ThreeDStockMaterialsCompactListItemButtonsMaterial>;
  onEditButtonMouseEnter?: () => void;
}

function ThreeDStockMaterialsCompactListItemButtons({
  material,
  updateMaterialCache,
  onEditButtonMouseEnter
}: ThreeDStockMaterialsCompactListItemButtonsProps) {
  const { handleToggleFavorite, toggleMaterialFavoriteLoading } =
    useMaterialFavoriteButton({
      material,
      updateMaterialCache
    });

  return (
    <div className="flex justify-between gap-1 z-1 min-w-0">
      <div className="rounded-full flex min-w-0 bg-blue-500">
        <CheckPermissions
          action={MaterialsPermissions.READ_MATERIAL_FAVORITE_BUTTON}
        >
          <ButtonHelper
            dataGa="materials-material-favorite-button"
            className={
              material.favorite
                ? 'py-2.5 pl-5 pr-5 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out'
                : 'py-2.5 pl-5 pr-5 rounded-full inline-flex items-center leading-5 focus:ring-base text-white bg-blue-500 hover:bg-blue-600 shadow-sm hover:shadow-md max-w-full transition-colors duration-150 ease-out'
            }
            onClick={handleToggleFavorite}
            disabled={toggleMaterialFavoriteLoading}
          >
            {material.favorite ? (
              <span>
                <Translate id={words.saved} />
              </span>
            ) : (
              <div className="text-sm whitespace-nowrap truncate space-x-1">
                <span>
                  <Translate id={words.save} />
                </span>
                <span className="hidden group-hover:inline group-focus-within:inline space-x-1">
                  <span className="text-blue-200 lowercase">
                    <Translate id={words.to} />
                  </span>
                  <span>
                    <Translate id={words.favorites} />
                  </span>
                </span>
              </div>
            )}
          </ButtonHelper>
        </CheckPermissions>
      </div>

      <div className="flex gap-1 xl:pr-2">
        <CheckPermissions
          action={MaterialsPermissions.READ_MATERIAL_DOWNLOAD_BUTTON}
        >
          <DownloadMaterialButton
            className="py-2.5 pl-2.5 pr-2.5 rounded-full inline-flex items-center leading-5 focus:ring-base text-gray-900 bg-gray-200 dark:text-white dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
            dataGa="materials-material-download-button"
            icon={IconsEnum.DOWNLOAD_SOLID}
            iconClassName="h-5 w-5"
            materialUuid={material.uuid}
            tooltipI18nText={words.download}
            tooltipSingleton
          />
        </CheckPermissions>

        <CheckPermissions
          action={MaterialsPermissions.READ_MATERIAL_DOWNLOAD_SEPARATELY_BUTTON}
        >
          <DownloadMaterialSeparatelyButton
            className="py-2.5 pl-2.5 pr-2.5 rounded-full inline-flex items-center leading-5 focus:ring-base text-gray-900 bg-gray-200 dark:text-white dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
            dataGa="materials-material-download-separately-button"
            icon={IconsEnum.DOWNLOAD_SOLID}
            iconClassName="h-5 w-5"
            materialUuid={material.uuid}
            tooltipI18nText={words.download}
            tooltipSingleton
          />
        </CheckPermissions>

        <DropdownHelper
          className="relative flex items-center justify-center"
          buttonClassName="py-2.5 pl-2.5 pr-2.5 rounded-full inline-flex items-center leading-5 focus:ring-base text-gray-900 bg-gray-200 dark:text-white dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
          icon={IconsEnum.DOTS_HORIZONTAL_OUTLINE}
          popoverClassName="flex flex-col items-end gap-1"
          dropdownPlacement={PopoverPlacement.TOP_END}
        >
          <CheckPermissions
            action={
              MaterialsPermissions.READ_MATERIAL_SUBMIT_TO_TASK_OR_PROJECT_BUTTON
            }
          >
            <SubmitMaterialToProjectOrTaskModalButton
              dataGa="materials-material-submit-to-task-or-project-button"
              className="py-2.5 pl-2.5 pr-5 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
              material={material}
              i18nText={words.attach}
              i18nTextClassName="inline"
              iconClassName="w-5 h-5"
            />
          </CheckPermissions>

          <CheckPermissions
            action={MaterialsPermissions.READ_MATERIAL_COPY_LINK_BUTTON}
          >
            <MaterialsCopyLinkButton
              className="py-2.5 pl-2.5 pr-5 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
              dataGa="materials-material-copy-link-button"
              icon={IconsEnum.LINK_SOLID}
              iconClassName="h-5 w-5"
              material={material}
              i18nText={words.copyLink}
              i18nTextClassName="inline"
            />
          </CheckPermissions>

          <CheckPermissions
            action={MaterialsPermissions.READ_MATERIAL_EDIT_BUTTON}
          >
            <NextLinkHelper
              onMouseEnter={onEditButtonMouseEnter}
              dataGa="materials-material-edit-link"
              href={MaterialPath.edit(material.nanoId)}
              className="py-2.5 pl-2.5 pr-5 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
            >
              <Icon icon={IconsEnum.PENCIL} className="w-5 h-5" />
              <span>
                <Translate id={words.edit} />
              </span>
            </NextLinkHelper>
          </CheckPermissions>
        </DropdownHelper>
      </div>
    </div>
  );
}

export default ThreeDStockMaterialsCompactListItemButtons;
